import "./_swap-details-modal.scss";

import {ReactComponent as ArrowRightIcon} from "../../../core/ui/icon/ArrowRight.svg";

import {
  convertFromBaseUnits,
  getAssetInFromSwapQuote,
  getAssetOutFromSwapQuote
} from "@tinymanorg/tinyman-js-sdk";

import Line from "../../../component/line/Line";
import ModalHeader from "../../../component/modal/header/ModalHeader";
import PriceImpactValue from "../../../component/price-impact/PriceImpactValue";
import PoolPriceInfo from "../../../pool/price-info/PoolPriceInfo";
import SwapDetailsModalDetailRow from "./detail-row/SwapDetailsModalDetailRow";
import SwapPriceDetailsModalPairAsset from "./pair-asset/SwapDetailsModalPairAsset";
import Modal from "../../../component/modal/Modal";
import SwapButton from "../../../component/swap-button/SwapButton";
import {useSwapFormState} from "../context/SwapFormContext";
import {
  calculateEstimatedSwapImpact,
  determineSwapAmounts,
  getPlatformFeeAmount,
  isFixedInputSwap
} from "../SwapForm.utils";
import {AsyncProcessState} from "../../../core/network/async-process/asyncProcessTypes";
import {getAssetUnitDisplayName} from "../../../asset/util/assetUtils";
import {getPlatformFeePercentage, getPlatformName} from "../../../core/widgetUtils";

interface SwapDetailsModalProps {
  isOpen: boolean;
  onClose: VoidFunction;
  asyncProcessState: AsyncProcessState;
}

function SwapDetailsModal({isOpen, onClose, asyncProcessState}: SwapDetailsModalProps) {
  const state = useSwapFormState();
  const {
    assetIn: {asset: assetIn},
    assetOut: {asset: assetOut},
    quote
  } = state;
  const {priceImpact, minimumReceived, maximumSent} = calculateEstimatedSwapImpact({
    state,
    quote
  });
  const {assetInAmount, assetOutAmount} = determineSwapAmounts(state);
  const isFixedInput = isFixedInputSwap(state);
  const platformFeePercentage = getPlatformFeePercentage();
  const platformName = getPlatformName();

  return renderContent();

  function renderContent() {
    if (assetIn && assetOut && quote) {
      return (
        <Modal
          contentLabel={"Swap details modal"}
          isOpen={isOpen}
          onClose={onClose}
          shouldCloseOnOverlayClick={true}
          customClassName={"swap-details-modal"}>
          <ModalHeader
            customClassName={"swap-details-modal__modal-header"}
            title={"Swap details"}
            closeButtonProps={{
              onClick: onClose,
              "aria-label": "Close swap details modal"
            }}
          />

          <div className={"swap-details-modal__pair-assets"}>
            <SwapPriceDetailsModalPairAsset
              asset={assetIn}
              amount={Number(assetInAmount)}
            />

            <ArrowRightIcon />

            <SwapPriceDetailsModalPairAsset
              asset={assetOut}
              amount={Number(assetOutAmount)}
            />
          </div>

          <Line margin={24} />

          <div className={"swap-details-modal__price-details"}>
            {/** TODO: ideally we should use description list here */}
            <SwapDetailsModalDetailRow
              title={"Price rate"}
              description={
                <PoolPriceInfo
                  asset_1={assetIn}
                  asset_1_reserves={Number(getAssetInFromSwapQuote(quote).amount)}
                  asset_2={assetOut}
                  asset_2_reserves={Number(getAssetOutFromSwapQuote(quote).amount)}
                  customClassName={"swap-details-modal__price-details__price-info"}
                />
              }
            />

            <SwapDetailsModalDetailRow
              title={isFixedInput ? "Minimum received" : "Maximum spent"}
              tooltipContent={
                isFixedInput
                  ? `Output is estimated. You will receive at least ${minimumReceived} ${getAssetUnitDisplayName(
                      assetOut
                    )}. Your transaction will revert if the price changes unfavorably more than 1%`
                  : `Output is estimated. You will spend at most ${maximumSent} ${getAssetUnitDisplayName(
                      assetIn
                    )}. Your transaction will revert if the price changes unfavorably more than 1%`
              }
              description={
                isFixedInput
                  ? `${minimumReceived} ${getAssetUnitDisplayName(assetOut)}`
                  : `${maximumSent} ${getAssetUnitDisplayName(assetIn)}`
              }
            />

            <SwapDetailsModalDetailRow
              title={"Platform fee"}
              tooltipContent={`${platformName} charges a service fee of ${
                platformFeePercentage || 0
              }% from each swap made with the swap widget.`}
              description={`${convertFromBaseUnits(
                assetIn.decimals,
                getPlatformFeeAmount({
                  assetInAmount: getAssetInFromSwapQuote(quote).amount
                })
              )} ${assetIn.unit_name}`}
            />

            <SwapDetailsModalDetailRow
              title={"Price impact"}
              tooltipContent={
                "Making a swap shifts the ratio of assets in the liquidity pool, causing this change in price per asset."
              }
              description={<PriceImpactValue priceImpact={priceImpact} />}
            />
          </div>

          <div className={"swap-details-modal__swap-button-container"}>
            <SwapButton
              asyncProcessState={asyncProcessState}
              customClassName={"swap-details-modal__swap-button-container__button"}
            />
          </div>
        </Modal>
      );
    }
    return null;
  }
}

export default SwapDetailsModal;
